import { defineComponent as _defineComponent } from 'vue'
import { watch, onMounted, ref,onUnmounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Loader from '@/components/loader.vue'

import DateSelector from '@/components/meeting-request-2/date-selector.vue'
import TimeSelector from '@/components/meeting-request-2/time-selector.vue'
import MeetingRescheduled  from '@/components/meeting-request-2/meeting-rescheduled.vue'

import { DataInstance, InviteLinks, Meetings, Userspaces } from '@busy-human/opt-library';
import { checkLoggedIn, getUserId, logout } from '@/util/auth';
import { httpsCallable } from 'firebase/functions';
import {functions} from '../util/firebase'
import { ISOTimeChunk, DayWithMomentChunk, RequestStage } from "@/components/meeting-request-2/meeting-request-types";

import { ISOTimestamp } from '@busy-human/gearbox'
import { Moment } from 'moment';

import { useMeetingRequestSchedule } from "@/components/meeting-request-2/schedule-mixin";
import { useUserspace } from '@/store/userspace';


export default /*#__PURE__*/_defineComponent({
  __name: 'meeting-reschedule',
  setup(__props, { expose }) {
  expose();

const route = useRoute();
const router = useRouter();

//HTTP Functions
const getUserAvailability = httpsCallable<{uid: string, year: number, month: number}, {startTime: string, endTime: string}[]>(functions, 'getUserAvailability')
const rescheduleMeeting = httpsCallable<{data: Meetings.Model, meetingId: string}, DataInstance<Meetings.Model>>(functions, 'rescheduleMeeting');

const noScroll = ref<boolean>(false)
const loading = ref<boolean>(true)
const stage = ref<RequestStage>(RequestStage.DATE);
const error = ref<string | null>(null);

//Meeting Creator User Variables
const user = ref<DataInstance<Userspaces.Model>  | null>(null);

//Invite Creator User Variables
const person = ref<DataInstance<Userspaces.Model> | null>(null);
const events = ref<ISOTimeChunk[]>([])

//Meeting Variables
const invite = ref<InviteLinks.Model | null>(null);
const request = ref<Meetings.Model>(Meetings.Defaults());
const meetingFound = ref(false)
const selectedDate = ref<Date|string>()

//Calendar Variables
const timesAvailable = ref<DayWithMomentChunk[]>([])
const timesAvailableForDay = ref<DayWithMomentChunk[]>([])
const env = ref(ENVIRONMENT)

const meetingLength = ref<number>(0);

// const { meetingDate } = useMeetingRequestSchedule({
//     request: ref(Meetings.Defaults()),
//     meetingLength: meetingLength,
//     env: env
// });


onMounted(()=>{
    sessionStorage.setItem('rescheduleStage','1')
    // console.log("STAGE:",stage.value)
})

onUnmounted(()=>{ 
    killMixin()
})

watch(() => route.params.meetingId, async id => {
    if(id && route.params.meetingId) {
        user.value = useUserspace().user;

        let meetingId = id.toString()
        let meetingRef
        try{
            meetingRef = await Meetings.Collection.fetchDoc(meetingId)
            meetingFound.value = true
        }catch(e){
            meetingFound.value = false
            loading.value = false
            return
        }
        const requestData: DataInstance<Meetings.Model> = {
            ... meetingRef.data() as Meetings.Model,
            $id: meetingId
        }

        //If the user isn't a part of the meeting, they shouldn't be able to reschedule it
        if(user.value.$id != requestData.payer && user.value.$id != requestData.collector){
            loading.value = false
            return
        }

        let inviteData = InviteLinks.Defaults()

        if(requestData.payer && requestData.payer != user.value.$id){
            person.value = {
                ...(await Userspaces.Collection.fetchDoc(requestData.payer)).data(),
                $id: requestData.payer
            }
            inviteData.payer = requestData.payer
        }
        else if(requestData.collector && requestData.collector != user.value.$id){
            person.value = {
                ...(await Userspaces.Collection.fetchDoc(requestData.collector)).data(),
                $id: requestData.collector
            }
            inviteData.collector = requestData.collector
        }

        if(requestData.startTime && requestData.endTime){
            let millisDiff = Math.abs(new Date(requestData.endTime).getTime() - new Date(requestData.startTime).getTime())
            let minuteDiff = (millisDiff / (1000 * 60))

            inviteData.minutes = minuteDiff
        }else{
            error.value = "Error: Couldn't Calculate Meeting Duration"
        }

        requestData.meetingLength = inviteData.minutes

        const result = await updateUserAvailability(inviteData.collector || inviteData.payer);
        events.value = result

        //Assign the values at the end after they're been fully initialized
        invite.value = inviteData
        request.value = requestData

        loading.value = false;
    }
}, {immediate: true})

watch(()=>stage.value, stage =>{
    let stageToStore = stage.toString()
    // console.log("stage:",stage)
    sessionStorage.setItem('rescheduleStage',stageToStore)
},{immediate: true})

const doRescheduleMeeting = async () => {
    try {

        loading.value = true
        error.value = null;

        request.value!.endTime = new Date(new Date(request.value!.startTime).getTime() + request.value!.meetingLength! * 60000).toISOString();
        
        await rescheduleMeeting({data: request.value as Meetings.Model, meetingId: request.value!.$id});

        stage.value = RequestStage.CONGRATS
        loading.value = false;

    } catch(e) {
        console.error(e);
        loading.value = false
        error.value = "Error while rescheduling meeting"
        return;
    }
}

//Helper Functions


const formatTime = (time: Date) =>{
    const timeOptions: Intl.DateTimeFormatOptions =  {hour: 'numeric', minute: "numeric"}
    return new Intl.DateTimeFormat('en-US', timeOptions).format(time)
}

const routeTo = (route:string) =>{
    router.push(`/${route}`);
}

const setSelectedDate = (date: string | Date) =>{
    selectedDate.value = date
    // console.log("Selected:",selectedDate.value)
    let newStartTime = new Date(date).toISOString()
    meetingDate.value = new Date(date)
}

const updateTimesAvailable = (value: DayWithMomentChunk[])=>{
    timesAvailable.value = value
}

const { meetingDate, updateUserAvailability, killMixin } = useMeetingRequestSchedule({
    request: request,
    meetingLength: meetingLength,
    env: env
});

const { checkPreviousDay } = useMeetingRequestSchedule(null)
const updateRequest = (meeting: Meetings.Model)=>{
    request.value = meeting
}


const __returned__ = { route, router, getUserAvailability, rescheduleMeeting, noScroll, loading, stage, error, user, person, events, invite, request, meetingFound, selectedDate, timesAvailable, timesAvailableForDay, env, meetingLength, doRescheduleMeeting, formatTime, routeTo, setSelectedDate, updateTimesAvailable, meetingDate, updateUserAvailability, killMixin, checkPreviousDay, updateRequest, watch, onMounted, ref, onUnmounted, computed, useRoute, useRouter, Loader, DateSelector, TimeSelector, MeetingRescheduled, DataInstance, InviteLinks, Meetings, Userspaces, checkLoggedIn, getUserId, logout, httpsCallable, functions, ISOTimeChunk, DayWithMomentChunk, RequestStage, ISOTimestamp, Moment, useMeetingRequestSchedule, useUserspace }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})